/**
 * Continue Delivery navigation helpers.
 * Flag lives on Verification context (session storage): isContinueDelivery.
 *
 * Two independent axes — do not conflate:
 * - SERVICE (?service=): product path. Insurance-only (?service=1, no 2) →
 *   no Knowledge Hub artifacts. Trade/Quote still follow workflow.
 * - WORKFLOW (verification_workflow_id): Trade/Quote.
 *   1 = trade+quote, 2 = neither, 3 = quote only.
 *   Verify Only (2) still shows artifacts when dealer has KH (no ?service=).
 *
 * Artifacts / KW when session includes KH (serviceId "2") or dealership has KH.
 * Insurance-only service never shows them.
 *
 * Combined CD order: carriers → Continue Delivery → quote
 *   → KW + artifacts on thank-you → congrats
 */

export function getServiceIds(verification) {
  return (verification?.customer?.serviceId || []).map(String).filter(Boolean);
}

/**
 * Customer insurance-only service from URL (?service=1, no service=2).
 * Set on Verification context at Consent — not inferred from serviceId alone
 * (dashboard with only insurance enabled must still follow workflow trade/quote).
 */
export function isInsuranceOnlyService(verification) {
  return !!verification?.isInsuranceOnlyService;
}

/**
 * KH artifacts: session has service 2, or (no insurance-only restriction) dealer has KH.
 * Insurance-only service never shows artifacts. Workflow Verify Only still can.
 */
export function includesKnowledgeHubService(verification) {
  if (isInsuranceOnlyService(verification)) return false;
  return (
    getServiceIds(verification).includes("2") ||
    !!verification?.checkService?.knowledgeHub
  );
}

/** Post-pull / skip-artifacts route from workflow only (not ?service=). */
export function getPostPullNavTarget(verification) {
  const workflow = parseInt(verification?.workflow);
  if (workflow === 3) return "/verification/requestQuote";
  if (workflow === 2) return "/verification/thankYou";
  return "/verification/tradeVehicle";
}

/**
 * Continue Delivery → thank you for Verify Only workflow or customer-table
 * skip; otherwise quote. Service path does not change this.
 */
export function getContinueDeliveryNavTarget(verification) {
  if (
    parseInt(verification?.workflow) === 2 ||
    verification?.isFromCustomerTable
  ) {
    return "/verification/thankYou";
  }
  return "/verification/requestQuote";
}

export function getArtifactItems(verification) {
  return Array.isArray(verification?.artifacts)
    ? verification.artifacts
    : verification?.artifacts?.items || [];
}

export function hasArtifacts(verification) {
  return getArtifactItems(verification).length > 0;
}

/** Pull / overlay: artifacts + walkthrough enabled + KH in this session */
export function shouldShowKnowledgeHub(verification) {
  return (
    hasArtifacts(verification) &&
    !!verification?.isWalkthrough &&
    includesKnowledgeHubService(verification)
  );
}

/** Continue Delivery button → mark CD, then quote/thankYou first. */
export function navigateContinueDelivery(navigate, verification, fallbackPath) {
  verification.setIsContinueDelivery(true);
  // Only reopen artifact playback for combined / KH sessions
  if (includesKnowledgeHubService(verification)) {
    verification.setIsArtifactOpen?.(false);
  }
  navigate(fallbackPath, { replace: true });
}

/**
 * After quote (yes or no): always thank you.
 * Combined / KH sessions play KW + artifacts on thank-you; insurance-only skips them.
 */
export function navigateAfterQuote(navigate, verification) {
  const playArtifactsOnThankYou =
    hasArtifacts(verification) && includesKnowledgeHubService(verification);

  if (playArtifactsOnThankYou) {
    verification.setIsArtifactOpen?.(false);
    // Ensure thank-you treats this as an artifacts pass even if flags were
    // already marked open from mid-pull / init races.
    verification.setIsContinueDelivery?.(true);
    try {
      sessionStorage.setItem("playArtifactsAfterQuote", "true");
    } catch {
      /* ignore */
    }
  }

  navigate(`/verification/thankYou`, {
    replace: true,
    state: { playArtifactsAfterQuote: playArtifactsOnThankYou },
  });
}

/** After KW + artifacts when using /knowledgeHub route → thank you. */
export function navigateAfterContinueDeliveryArtifacts(navigate, verification) {
  verification?.setIsContinueDelivery?.(false);
  verification?.setIsArtifactOpen?.(true);
  navigate(`/verification/thankYou`, { replace: true });
}
